import axios from 'axios';
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { getApiUrl } from '@lib/config';
import Cookies from 'js-cookie';

// Create axios instance with relative base URL
// This ensures all API calls use the same domain as the page,
// preventing CORS issues when using multiple domains
const httpClient: AxiosInstance = axios.create({
  baseURL: getApiUrl(''), // Returns '' (empty) when using relative URLs
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Send cookies with requests
});

// Request interceptor to add auth token
httpClient.interceptors.request.use(
  (config) => {
    const idToken = Cookies.get('idToken');
    if (idToken) {
      config.headers.Authorization = `Bearer ${idToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
httpClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Unauthorized - clear tokens and redirect to login
      Cookies.remove('idToken');
      Cookies.remove('accessToken');
      Cookies.remove('refreshToken');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default httpClient;

// Helper function for GET requests
export const get = <T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => {
  return httpClient.get<T>(url, config);
};

// Helper function for POST requests
export const post = <T = any>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig
): Promise<AxiosResponse<T>> => {
  return httpClient.post<T>(url, data, config);
};

// Helper function for PUT requests
export const put = <T = any>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig
): Promise<AxiosResponse<T>> => {
  return httpClient.put<T>(url, data, config);
};

// Helper function for DELETE requests
export const del = <T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => {
  return httpClient.delete<T>(url, config);
};
